import { useEffect, useMemo, useRef, useState } from "react";
import { Activity, X } from "@fg/icons";
import { cn } from "@/lib/utils";
import { outboundBytes } from "./meeting/uploadStats";

/**
 * Teams-style "Call quality" overlay. Polls RTCPeerConnection.getStats()
 * once per second for every active peer and renders the per-leg
 * bitrate, packet-loss %, and round-trip-time in a small floating
 * panel. Pure client-side; no server roundtrip — useful for
 * diagnosing noisy calls without coturn / SFU instrumentation.
 *
 * Lifecycle: mounted while the user has the panel open. The poll
 * interval clears on unmount; getStats itself is cheap (~0.2 ms per
 * peer in Chromium).
 */
export interface CallStatsRow {
    connectionId: string;
    label: string;
    /** What we upload to this peer, audio + video (+ screen), in kbps. */
    sendKbps: number;
    /** Inbound video bitrate in kbps. */
    recvKbps: number;
    /** Inbound packet-loss as a 0-1 fraction. */
    lossPct: number;
    /** Round-trip-time in milliseconds. Null when the candidate-pair stats lack the field. */
    rttMs: number | null;
}

export function CallStatsPanel({
    pcs,
    peerLabels,
    onClose
}: {
    pcs: Record<string, RTCPeerConnection>;
    peerLabels: Record<string, string>;
    onClose: () => void;
}) {
    const stats = useCallStatsPoll(pcs, peerLabels);
    // A mesh meeting uploads one copy per person: this total is what limits a meeting on your connection.
    const uploadKbps = stats.reduce((sum, s) => sum + s.sendKbps, 0);
    return (
        <aside
            role="region"
            aria-label="Call quality"
            className="pointer-events-auto absolute right-3 top-14 z-30 w-72 rounded-md border border-white/10 bg-zinc-900/90 p-2 text-xs text-white shadow-xl backdrop-blur-sm"
        >
            <header className="mb-1 flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/70">
                <Activity className="h-3 w-3" aria-hidden />
                Call quality
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Hide call quality"
                    className="ml-auto rounded p-0.5 hover:bg-white/10"
                >
                    <X className="h-3 w-3" />
                </button>
            </header>
            {stats.length > 0 ? (
                <div className="mb-1.5 flex items-center justify-between rounded bg-white/5 px-1.5 py-1 text-[10px]">
                    <span className="text-white/60">Your upload, all peers</span>
                    <span className="tabular-nums">{uploadKbps} kbps</span>
                </div>
            ) : null}
            {stats.length === 0 ? (
                <p className="py-2 text-center text-[10px] text-white/50">No peer connections yet.</p>
            ) : (
                <ul className="flex flex-col gap-1.5">
                    {stats.map((s) => (
                        <li key={s.connectionId} className="rounded bg-white/5 p-1.5">
                            <div className="mb-0.5 truncate font-medium">{s.label}</div>
                            <div className="grid grid-cols-3 gap-1 text-[10px]">
                                {/* Upload is information, not a verdict: a silent participant on audio rightly sends almost nothing. */}
                                <Stat label="↑" value={`${s.sendKbps} kbps`} tone="neutral" />
                                <Stat label="↓" value={`${s.recvKbps} kbps`} tone={tone(s.recvKbps, [80, 250])} />
                                <Stat
                                    label="loss"
                                    value={`${(s.lossPct * 100).toFixed(1)}%`}
                                    tone={s.lossPct > 0.05 ? "bad" : s.lossPct > 0.01 ? "warn" : "good"}
                                />
                                <Stat
                                    label="rtt"
                                    value={s.rttMs === null ? "—" : `${Math.round(s.rttMs)} ms`}
                                    tone={s.rttMs === null ? "neutral" : s.rttMs > 250 ? "bad" : s.rttMs > 120 ? "warn" : "good"}
                                />
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </aside>
    );
}

type Tone = "good" | "warn" | "bad" | "neutral";

function Stat({ label, value, tone }: { label: string; value: string; tone: Tone }) {
    return (
        <div className="rounded bg-black/30 p-1">
            <div className="text-[9px] uppercase tracking-wider text-white/50">{label}</div>
            <div className={cn(
                "tabular-nums",
                tone === "good"  && "text-emerald-400",
                tone === "warn"  && "text-amber-400",
                tone === "bad"   && "text-red-400",
                tone === "neutral" && "text-white/70"
            )}>
                {value}
            </div>
        </div>
    );
}

function tone(kbps: number, thresholds: [number, number]): Tone {
    if (kbps <= 0) return "neutral";
    if (kbps < thresholds[0]) return "bad";
    if (kbps < thresholds[1]) return "warn";
    return "good";
}

interface PrevSample {
    timestamp: number;
    bytesSent: number;
    bytesReceived: number;
    packetsLost: number;
    packetsReceived: number;
}

/**
 * Polls every connected peer's getStats() once a second. Bitrate is
 * derived as a delta against the previous poll so we never have to
 * trust a stat type that ships only "rate" (which Chromium and
 * Firefox disagree on). Packet-loss is the cumulative
 * <c>packetsLost / (packetsLost + packetsReceived)</c> ratio.
 */
function useCallStatsPoll(
    pcs: Record<string, RTCPeerConnection>,
    labels: Record<string, string>
): CallStatsRow[] {
    const [rows, setRows] = useState<CallStatsRow[]>([]);
    const prevRef = useRef<Record<string, PrevSample>>({});

    // Stash the latest pcs / labels in refs so the interval doesn't
    // need to re-create on every render. The deps array intentionally
    // tracks only the *connection ids* — when peers join or leave,
    // the array length changes, which is the meaningful trigger.
    const pcKeys = useMemo(() => Object.keys(pcs).sort().join(","), [pcs]);

    useEffect(() => {
        let cancelled = false;
        const tick = async () => {
            const next: CallStatsRow[] = [];
            for (const [id, pc] of Object.entries(pcs)) {
                if (pc.connectionState !== "connected") continue;
                try {
                    const report = await pc.getStats();
                    // Everything we send this peer (audio, camera, screen; RTP headers included) — the upload.
                    const sent = outboundBytes(report.values() as Iterable<Record<string, unknown>>);
                    const bytesSent = sent.audio + sent.video;
                    let bytesReceived = 0;
                    let packetsLost = 0;
                    let packetsReceived = 0;
                    let rttMs: number | null = null;
                    report.forEach((stat: RTCStats & Record<string, unknown>) => {
                        if (stat.type === "inbound-rtp" && (stat as RTCStats & { kind?: string }).kind === "video") {
                            bytesReceived += Number((stat as Record<string, unknown>).bytesReceived ?? 0);
                            packetsLost += Number((stat as Record<string, unknown>).packetsLost ?? 0);
                            packetsReceived += Number((stat as Record<string, unknown>).packetsReceived ?? 0);
                        } else if (stat.type === "candidate-pair" && (stat as Record<string, unknown>).nominated === true) {
                            const rtt = (stat as Record<string, unknown>).currentRoundTripTime;
                            if (typeof rtt === "number") rttMs = rtt * 1000;
                        }
                    });

                    const now = performance.now();
                    const prev = prevRef.current[id];
                    let sendKbps = 0;
                    let recvKbps = 0;
                    if (prev) {
                        const seconds = (now - prev.timestamp) / 1000;
                        if (seconds > 0) {
                            sendKbps = Math.max(0, Math.round((bytesSent - prev.bytesSent) * 8 / 1000 / seconds));
                            recvKbps = Math.max(0, Math.round((bytesReceived - prev.bytesReceived) * 8 / 1000 / seconds));
                        }
                    }
                    prevRef.current[id] = { timestamp: now, bytesSent, bytesReceived, packetsLost, packetsReceived };

                    const totalReceived = packetsReceived + packetsLost;
                    const lossPct = totalReceived > 0 ? packetsLost / totalReceived : 0;

                    next.push({
                        connectionId: id,
                        label: labels[id] ?? id.slice(0, 6),
                        sendKbps,
                        recvKbps,
                        lossPct,
                        rttMs
                    });
                } catch {
                    // getStats can throw on a closing connection — drop
                    // that peer for this tick; next round picks it up
                    // again if the pc list still references it.
                }
            }
            if (!cancelled) setRows(next);
        };

        // Fire once immediately so the panel doesn't stay empty for a
        // full tick on first open.
        void tick();
        const handle = window.setInterval(() => void tick(), 1000);
        return () => {
            cancelled = true;
            window.clearInterval(handle);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pcKeys]);

    return rows;
}
